import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpCircle, RefreshCw, ShieldAlert } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useSession } from "@/hooks/useSession";
import { api, type UpdateItem, type UpdateJob, type UpdatePolicy } from "@/lib/api";
import { cn } from "@/lib/utils";

const CHANNELS = ["rules", "plugins", "bw_engine", "platform", "migrations", "os_runtime"] as const;
const CHANNEL_LABEL: Record<string, string> = {
  rules: "Security rules", plugins: "Plugins", bw_engine: "BunkerWeb engine",
  platform: "NXShield platform", migrations: "DB migrations", os_runtime: "OS / runtime",
};
const RISK_TONE: Record<string, string> = {
  low: "text-emerald-300", medium: "text-amber-300", high: "text-red-300",
  unknown: "text-zinc-400",
};
const STATUS_TONE: Record<string, string> = {
  discovered: "text-zinc-400", assessed: "text-sky-300", staged: "text-sky-300",
  approved: "text-violet-300", scheduled: "text-violet-300",
  backing_up: "text-amber-300", applying: "text-amber-300",
  verifying: "text-amber-300", successful: "text-emerald-300",
  failed: "text-red-300", recovery_required: "text-red-300",
  rolled_back: "text-amber-300", cancelled: "text-zinc-500",
};

export default function Updates() {
  const qc = useQueryClient();
  const { user } = useSession();
  const isOwner = user?.role === "owner";
  const canOperate = isOwner || user?.role === "operator";
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: policies } = useQuery({
    queryKey: ["update-policies"],
    queryFn: () => api.get<Record<string, UpdatePolicy>>("/api/updates/policies"),
  });
  const { data: items } = useQuery({
    queryKey: ["update-items"],
    queryFn: () => api.get<{ items: UpdateItem[]; executor?: { available: boolean; reason: string } }>("/api/updates/items"),
    refetchInterval: 15_000,
  });
  const executorDown = items?.executor ? !items.executor.available : false;
  const { data: jobs } = useQuery({
    queryKey: ["update-jobs"],
    queryFn: () => api.get<{ items: UpdateJob[] }>("/api/updates/jobs"),
    refetchInterval: 10_000,
  });

  const check = useMutation({
    mutationFn: () => api.post("/api/updates/check"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["update-items"] }),
  });
  const policyMut = useMutation({
    mutationFn: ({ channel, mode }: { channel: string; mode: string }) =>
      api.put(`/api/updates/policies/${channel}`, { mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["update-policies"] }),
    onError: (e) => alert(`Policy change requires owner + fresh re-auth: ${e}`),
  });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "stage" | "approve" | "cancel" }) =>
      api.post(`/api/updates/items/${id}/${action}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["update-items"] }),
    onError: (e) => alert(String(e)),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Update Center"
        description="Channels are independent streams. Nothing applies without an approved job; a failed backup always stops the upgrade."
      >
        {canOperate && (
          <Button size="sm" onClick={() => check.mutate()} disabled={check.isPending}>
            <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", check.isPending && "animate-spin")} />
            Check now
          </Button>
        )}
      </PageHeader>

      {/* truthful executor status — worker self-reports via heartbeat; when
          the deployment executor isn't provisioned (prod: no docker/stack/
          backups mounts) this must say so, not imply applies are possible */}
      {items?.executor && (
        <div className={cn(
          "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
          executorDown
            ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
            : "border-emerald-500/30 bg-emerald-500/5 text-emerald-300/80",
        )}>
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Execution {executorDown ? "disabled" : "enabled"} on this deployment
            {items.executor.reason ? ` — ${items.executor.reason}` : ""}.
            {executorDown && " Discovery and staging still work; approve+apply is refused until the worker gains the executor mounts (separate approval required)."}
          </span>
        </div>
      )}

      {/* per-channel policy modes */}
      <div className="grid gap-3 md:grid-cols-3">
        {CHANNELS.map((ch) => {
          const pol = policies?.[ch];
          return (
            <Card key={ch} className="p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{CHANNEL_LABEL[ch]}</div>
                <Badge variant={pol?.mode === "automatic" ? "warning" : "secondary"}>
                  {pol?.mode ?? "notify"}
                </Badge>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Select
                  className="h-7 flex-1 text-xs"
                  value={pol?.mode ?? "notify"}
                  disabled={!isOwner}
                  onChange={(e) => policyMut.mutate({ channel: ch, mode: e.target.value })}
                >
                  <option value="notify">notify only</option>
                  <option value="stage">stage only</option>
                  <option value="scheduled">owner-scheduled</option>
                  <option value="automatic">automatic (bounded)</option>
                </Select>
              </div>
              {pol?.mode === "automatic" && (
                <div className="mt-1.5 text-[10px] text-muted-foreground">
                  bounded to {pol.allowed_components.join(", ") || "—"} · {pol.version_range}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* discovered items */}
      <Card>
        <div className="border-b px-4 py-3 text-sm font-medium">Discovered updates</div>
        <Table>
          <THead>
            <TR><TH>Component</TH><TH>Channel</TH><TH>Current → New</TH><TH>Risk</TH><TH>Status</TH><TH></TH></TR>
          </THead>
          <TBody>
            {(items?.items ?? []).map((i) => (
              <>
                <TR key={i.id} className="cursor-pointer" onClick={() => setExpanded(expanded === i.id ? null : i.id)}>
                  <TD className="font-medium">{i.component}</TD>
                  <TD className="text-xs">{CHANNEL_LABEL[i.channel] ?? i.channel}</TD>
                  <TD className="font-mono text-xs">{i.current_version || "?"} → {i.version}</TD>
                  <TD className={cn("text-xs", RISK_TONE[i.risk])}>{i.risk}</TD>
                  <TD className={cn("text-xs", STATUS_TONE[i.status])}>{i.status}</TD>
                  <TD className="text-right">
                    {canOperate && i.status === "assessed" && (
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); act.mutate({ id: i.id, action: "stage" }); }}>stage</Button>
                    )}
                    {isOwner && i.status === "staged" && (
                      <Button size="sm" disabled={executorDown}
                        title={executorDown ? "Executor unavailable — approval refused by the API" : undefined}
                        onClick={(e) => { e.stopPropagation(); act.mutate({ id: i.id, action: "approve" }); }}>approve+apply</Button>
                    )}
                    {canOperate && ["staged", "approved", "scheduled"].includes(i.status) && (
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); act.mutate({ id: i.id, action: "cancel" }); }}>cancel</Button>
                    )}
                  </TD>
                </TR>
                {expanded === i.id && (
                  <TR key={i.id + "-x"}><TD colSpan={6} className="bg-accent/20">
                    {i.requires_review && (
                      <div className="mb-2 flex items-start gap-2 text-xs text-amber-300">
                        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <ul className="list-inside list-disc">
                          {i.review_reasons.map((r, n) => <li key={n}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>source: <span className="font-mono">{i.source || "—"}</span></div>
                      <div>digest: <span className="font-mono">{i.digest?.slice(0, 24) || "—"}</span></div>
                      <div>discovered: {new Date(i.discovered_at).toLocaleString()}</div>
                    </div>
                  </TD></TR>
                )}
              </>
            ))}
          </TBody>
        </Table>
        {(items?.items ?? []).length === 0 && (
          <EmptyState icon={ArrowUpCircle} title="No updates discovered"
            hint="Run “Check now” to compare the release manifest against installed pins." />
        )}
      </Card>

      {/* jobs / history */}
      <Card>
        <div className="border-b px-4 py-3 text-sm font-medium">Update jobs &amp; release history</div>
        <Table>
          <THead>
            <TR><TH>Job</TH><TH>Status</TH><TH>Requested by</TH><TH>Approved by</TH><TH>Backup</TH><TH>Recovery point</TH><TH>Created</TH></TR>
          </THead>
          <TBody>
            {(jobs?.items ?? []).map((j) => (
              <TR key={j.id}>
                <TD className="font-mono text-xs">{j.id.slice(0, 8)}</TD>
                <TD className={cn("text-xs", STATUS_TONE[j.status])}>{j.status}</TD>
                <TD className="text-xs">{j.requested_by}</TD>
                <TD className="text-xs">{j.approved_by || "—"}</TD>
                <TD className="font-mono text-[10px]">{j.backup_ref || "—"}</TD>
                <TD className="text-xs">{j.recovery_point || "—"}</TD>
                <TD className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleString()}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {(jobs?.items ?? []).length === 0 && (
          <EmptyState icon={ArrowUpCircle} title="No update jobs" hint="Jobs appear once an update is approved." />
        )}
      </Card>
    </div>
  );
}
